/**
 * A section of the settings page (ADR 0172): what each field shows, its status
 * and error, and a change to send, as Mappings → Email and Network access save
 * theirs. One save at a time for the section; changes made meanwhile combine.
 * The API checks every value again, and audits each change.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { ApiError } from "../../api/client";
import type { AppSettings } from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";

export function useSettingsSection<K extends keyof AppSettings>(settings: AppSettings) {
  type Changes = Partial<Pick<AppSettings, K>>;
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Partial<Record<K, string>>>({});
  // The field "Saved." shows under: the last one changed, once confirmed.
  const [lastSaved, setLastSaved] = useState<K | null>(null);
  const lastTouched = useRef<K | null>(null);
  const save = useMutation({
    mutationFn: (changes: Changes) => api.updateSettings(changes),
    onSuccess: (result, changes) => {
      queryClient.setQueryData(["settings"], result);
      const keys = Object.keys(changes) as K[];
      setErrors((old) => without(old, keys));
      const touched = lastTouched.current;
      setLastSaved(touched !== null && touched in changes ? touched : (keys[0] ?? null));
    },
    onError: (error, changes) => {
      const keys = Object.keys(changes) as K[];
      const named = error instanceof ApiError ? error.fieldErrors.filter((e) => e.field in changes) : [];
      const failure = error instanceof Error ? error.message : "Something went wrong.";
      // Each refused field says why; a refusal naming none (or no answer)
      // shows under every field that was sent.
      const reasons = Object.fromEntries(
        named.length > 0 ? named.map((e) => [e.field, e.message]) : keys.map((k) => [k, failure]),
      ) as Partial<Record<K, string>>;
      setErrors((old) => ({ ...old, ...reasons }));
      // A refusal may come from a change made elsewhere: show what's saved.
      if (error instanceof ApiError && error.status < 500) {
        return queryClient.invalidateQueries({ queryKey: ["settings"] });
      }
      return undefined;
    },
  });
  const { send, saving } = useOneSaveAtATime(save, (queued: Changes, value: Changes) => ({
    ...queued,
    ...value,
  }));

  // What's saved, from the cache: a save's answer is there at once, while the
  // query passes it on a moment later, and a field shouldn't flash its old
  // value between.
  const saved = queryClient.getQueryData<AppSettings>(["settings"]) ?? settings;

  function shown<P extends K>(key: P): AppSettings[P] {
    const pending = saving?.value[key];
    return pending === undefined ? saved[key] : pending;
  }

  function change(changes: Changes) {
    const keys = Object.keys(changes) as K[];
    lastTouched.current = keys[0] ?? null;
    setLastSaved(null);
    setErrors((old) => without(old, keys));
    send(changes);
  }

  function statusOf(key: K): string | undefined {
    if (saving !== null && key in saving.value) {
      return "Saving…";
    }
    return key === lastSaved ? "Saved." : undefined;
  }

  return { saved, shown, change, statusOf, errors };
}

function without<T extends object>(values: T, keys: readonly string[]): T {
  return Object.fromEntries(Object.entries(values).filter(([key]) => !keys.includes(key))) as T;
}
